var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { appStore } from "../state/store";
import { ProgressUpdateModal } from "./ProgressUpdateModal";
export class Dashboard {
    constructor(app) {
        this.app = app;
        this.progressUpdateModal = null;
    }
    updateWelcomeMessage() {
        return __awaiter(this, void 0, void 0, function* () {
            const container = document.getElementById('welcome-narrative-container');
            if (!container)
                return; // Safety check
            const { user } = appStore.getState();
            try {
                const API_URL = process.env.API_BASE_URL;
                const response = yield fetch(`${API_URL}/api/dashboard-welcome-message`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        userProfile: user.profile.bfiProfile,
                        personalValues: user.onboardingInputs.personalValues
                    }),
                });
                if (!response.ok)
                    throw new Error('AI service failed');
                const data = yield response.json();
                container.innerHTML = `<p class="narrative-transition"><em>"${data.message}"</em></p>`;
            }
            catch (error) {
                console.error("Failed to fetch welcome message:", error);
                // Provide a graceful fallback message
                container.innerHTML = `<p class="narrative-transition"><em>"Welcome to your Decision-Making Nexus. Your stories define your past, your values define your 'why,' and this dashboard is where you write the story of your future, one intentionally planned day at a time. Let's begin."</em></p>`;
            }
        });
    }
    render() {
        const state = appStore.getState();
        const entities = Object.values(state.mainAppState.entities);
        // --- 1. Generate the Dynamic Entity List with Progress Tracking ---
        const itemsHtml = entities.map((entity) => {
            const progressPercent = (entity.goalValue && entity.goalValue > 0)
                ? (entity.currentValue / entity.goalValue) * 100
                : 0;
            // Create a meaningful label for the button instead of just "+".
            // This is the core of the Representational improvement.
            const progressText = (entity.goalValue && entity.goalUnit)
                ? `${entity.currentValue} / ${entity.goalValue}`
                : 'Update';
            const buttonTitle = (entity.goalValue && entity.goalUnit)
                ? `Update Progress (${entity.goalUnit})`
                : 'Update Progress';
            return `
                <li class="entity-item">
                    <div class="entity-info">
                        <span class="entity-name">${entity.name} (${entity.type})</span>
                        <div class="progress-bar-container-small" title="Progress: ${entity.currentValue} / ${entity.goalValue} ${entity.goalUnit}">
                            <div class="progress-bar-small" style="width: ${progressPercent}%;"></div>
                        </div>
                    </div>
                    <button class="button-stateful-update update-progress-btn" data-entity-id="${entity.id}" title="${buttonTitle}">
                        ${progressText}
                    </button>
                </li>
            `;
        }).join('');
        // --- 2. Define the Container for the Progress Update Modal ---
        const modalContainer = `<div id="modal-container"></div>`;
        // --- 3. Preserve Existing Modals and Buttons (Archetype & Feedback) ---
        const analysis = state.user.holisticStoryAnalysis;
        let archetypeButtonsHtml = '';
        const archetypesData = [
            { key: 'maker', title: 'Maker', description: 'For deep, focused creation.' },
            { key: 'manager', title: 'Manager', description: 'For communication & strategic work.' },
            { key: 'creative', title: 'Creative', description: 'For brainstorming & divergent thinking.' }
        ];
        if (analysis && analysis.archetypeAffinities) {
            const affinities = analysis.archetypeAffinities;
            const recommendedArchetype = Object.keys(affinities).reduce((a, b) => affinities[a] > affinities[b] ? a : b);
            archetypeButtonsHtml = archetypesData.map(({ key, title, description }) => {
                const isRecommended = key === recommendedArchetype;
                const score = affinities[key];
                const buttonClass = isRecommended ? 'button-secondary archetype-btn recommended-archetype' : 'button-secondary archetype-btn';
                return `
                    <button class="${buttonClass}" data-archetype="${key}">
                        <div class="archetype-btn-content"><strong>${title}:</strong> ${description}</div>
                        <span class="affinity-score" title="Your affinity score for this archetype is ${score}%">${score}%</span>
                    </button>
                `;
            }).join('');
        }
        else {
            archetypeButtonsHtml = archetypesData.map(({ key, title, description }) => `
                <button class="button-secondary archetype-btn" data-archetype="${key}"><strong>${title}:</strong> ${description}</button>
            `).join('');
        }
        const archetypeModal = `
            <div id="archetype-modal" class="modal" style="display: none;">
                <div class="modal-content">
                    <span id="close-archetype-modal" class="close-button">&times;</span>
                    <h3>Select Your Week Archetype</h3>
                    <p>The system's recommendation is highlighted and based on your Seven Stories analysis.</p>
                    <div class="button-container-vertical">${archetypeButtonsHtml}</div>
                </div>
            </div>
        `;
        const feedbackModal = `
            <div id="feedback-modal" class="modal" style="display: none;">
                <div class="modal-content">
                    <span id="close-feedback-modal" class="close-button">&times;</span>
                    <h3>Feedback for: The Decision-Making Nexus</h3>
                    <p>On a scale of 1-5, how effectively does this view help you decide what to do next?</p>
                    <div class="rating-buttons">
                        <button class="rating-btn" data-rating="1">1</button> <button class="rating-btn" data-rating="2">2</button> <button class="rating-btn" data-rating="3">3</button> <button class="rating-btn" data-rating="4">4</button> <button class="rating-btn" data-rating="5">5</button>
                    </div>
                    <div id="feedback-response" class="feedback-response"></div>
                </div>
            </div>
        `;
        const leaderButton = state.user.role === 'leader' ?
            `<button id="leader-view" class="button-secondary">Access Premium Dashboards</button>` : '';
        const welcomeNarrative = `<div id="welcome-narrative-container"><p class="narrative-transition"><em>Loading personalized welcome...</em></p></div>`;
        // --- 4. Assemble the Final Component HTML ---
        return `
            <div class="card">
                ${modalContainer}
                ${feedbackModal}
                <div class="feedback-button-container">
                    <button id="feedback-button" title="Send Feedback">💬</button>
                </div>
                ${archetypeModal}

                <h2>Dashboard: The Nexus of Coherent Agency</h2>
                ${welcomeNarrative}
                
                <div class="entity-list">
                    <h3>Current Projects</h3>
                    <ul>${itemsHtml.length > 0 ? itemsHtml : "<li>No projects to display.</li>"}</ul>
                </div>
                
                <div class="button-container">
                    ${leaderButton}
                    <button id="organize-week-btn" class="button-primary">Organize My Week</button>
                </div>
            </div>
        `;
    }
    attachEventListeners() {
        var _a;
        const archetypeModal = document.getElementById('archetype-modal');
        const organizeBtn = document.getElementById('organize-week-btn');
        const closeArchetypeBtn = document.getElementById('close-archetype-modal');
        const showModal = () => { if (archetypeModal)
            archetypeModal.style.display = 'block'; };
        const hideModal = () => { if (archetypeModal)
            archetypeModal.style.display = 'none'; };
        organizeBtn === null || organizeBtn === void 0 ? void 0 : organizeBtn.addEventListener('click', showModal);
        closeArchetypeBtn === null || closeArchetypeBtn === void 0 ? void 0 : closeArchetypeBtn.addEventListener('click', hideModal);
        // Use event delegation for the buttons inside the modal
        archetypeModal.addEventListener('click', (e) => {
            const target = e.target;
            const button = target.closest('.archetype-btn');
            if (button) {
                const archetype = button.getAttribute('data-archetype');
                if (archetype) {
                    appStore.setScheduleArchetype(archetype);
                    hideModal();
                    this.app.nextStep();
                }
            }
        });
        // --- Full implementation of feedback modal event listeners ---
        const feedbackModal = document.getElementById('feedback-modal');
        const feedbackBtn = document.getElementById('feedback-button');
        const closeFeedbackBtn = document.getElementById('close-feedback-modal');
        const ratingContainer = feedbackModal === null || feedbackModal === void 0 ? void 0 : feedbackModal.querySelector('.rating-buttons');
        feedbackBtn === null || feedbackBtn === void 0 ? void 0 : feedbackBtn.addEventListener('click', () => { if (feedbackModal)
            feedbackModal.style.display = 'block'; });
        if (closeFeedbackBtn)
            closeFeedbackBtn.onclick = () => { if (feedbackModal)
                feedbackModal.style.display = 'none'; };
        ratingContainer === null || ratingContainer === void 0 ? void 0 : ratingContainer.addEventListener('click', (e) => {
            const target = e.target;
            const rating = target.getAttribute('data-rating');
            const responseEl = document.getElementById('feedback-response');
            if (rating && responseEl) {
                let responseMessage = "";
                if (parseInt(rating) >= 4) {
                    responseMessage = `<p><strong>Thank you!</strong> Your rating of ${rating}/5 helps validate our current design approach.</p>`;
                }
                else {
                    responseMessage = `<p><strong>Thank you.</strong> We've noted a potential clarity issue. Your rating of ${rating}/5 has flagged this for review.</p>`;
                }
                responseEl.innerHTML = responseMessage;
                setTimeout(() => {
                    if (feedbackModal)
                        feedbackModal.style.display = 'none';
                    responseEl.innerHTML = '';
                }, 2500);
            }
        });
        // Global click listener to close modals when clicking outside of them
        window.onclick = (event) => {
            if (event.target == archetypeModal) {
                hideModal();
            }
            if (event.target == feedbackModal) {
                if (feedbackModal)
                    feedbackModal.style.display = "none";
            }
        };
        (_a = document.getElementById('leader-view')) === null || _a === void 0 ? void 0 : _a.addEventListener('click', () => {
            this.app.goToLeaderView();
        });
        const entityList = document.querySelector('#app .entity-list');
        entityList === null || entityList === void 0 ? void 0 : entityList.addEventListener('click', (e) => {
            const target = e.target;
            const button = target.closest('.update-progress-btn');
            if (button) {
                const entityId = button.getAttribute('data-entity-id');
                if (entityId) {
                    this.showProgressModal(entityId);
                }
            }
        });
        // Trigger the asynchronous fetch for the personalized welcome message
        this.updateWelcomeMessage();
    }
    showProgressModal(entityId) {
        const modalContainer = document.getElementById('modal-container');
        if (!modalContainer)
            return;
        // This logic is correct and does not need to be changed.
        this.progressUpdateModal = new ProgressUpdateModal(this.app, entityId);
        modalContainer.innerHTML = this.progressUpdateModal.render();
        this.progressUpdateModal.attachEventListeners();
    }
}
